import type {
  EmergencyContactDesignation,
  PersistedDraft,
  PublicSectionAnswer,
} from '../../application/models';
import {
  CountryCode,
  type CollectionItem,
  IsoDateTime,
  LocalDate,
  SectionId,
  TestamentId,
  type SingleAnswerValue,
} from '../../domain';

/*
 * A draft read back from storage — IndexedDB on the web, a JSON file in the apps — is plain data:
 * its value objects are rebuilt here, and every older shape of the record is read as today's.
 */

const reviveSingleValue = (value: SingleAnswerValue): SingleAnswerValue => {
  if (
    typeof value === 'object' &&
    value !== null &&
    'brand' in value &&
    value.brand === 'CountryCode' &&
    'value' in value &&
    typeof value.value === 'string'
  ) {
    return CountryCode.create(value.value);
  }
  return typeof value === 'object' && value !== null ? Object.freeze({ ...value }) : value;
};

const reviveCollectionItem = (item: CollectionItem): CollectionItem =>
  item.kind === 'money'
    ? Object.freeze({ ...item, amount: Object.freeze({ ...item.amount }) })
    : Object.freeze({ ...item });

const revivePublicAnswer = (answer: PublicSectionAnswer): PublicSectionAnswer => {
  if (answer.answerShape === 'single') {
    return Object.freeze({
      sectionId: SectionId.create(answer.sectionId.value),
      compartment: 'public',
      answerShape: 'single',
      value: reviveSingleValue(answer.value),
    });
  }
  if (answer.answerShape === 'counter') {
    return Object.freeze({
      sectionId: SectionId.create(answer.sectionId.value),
      compartment: 'public',
      answerShape: 'counter',
      value: answer.value,
    });
  }
  return Object.freeze({
    sectionId: SectionId.create(answer.sectionId.value),
    compartment: 'public',
    answerShape: 'collection',
    value: Object.freeze(answer.value.map(reviveCollectionItem)),
  });
};

// Drafts written before the emergency contact existed carry no such field at all: the stored
// record is whatever an older version of this schema wrote, not today's type.
/** Undefined where a draft predates the field: that draft was written before it existed. */
const reviveInstant = (instant: IsoDateTime | null | undefined): IsoDateTime | null =>
  instant === null || instant === undefined ? null : IsoDateTime.create(instant.value);

const reviveEmergencyContact = (
  contact: EmergencyContactDesignation | null | undefined,
): EmergencyContactDesignation | null =>
  contact === null || contact === undefined
    ? null
    : Object.freeze({
        ...contact,
        designatedAt: IsoDateTime.create(contact.designatedAt.value),
      });

export const revivePersistedDraft = (draft: PersistedDraft): PersistedDraft => {
  const owner = draft.testament.owner;
  const revision = draft.testament.revision;
  return Object.freeze({
    testament: Object.freeze({
      ...draft.testament,
      id: TestamentId.create(draft.testament.id.value),
      owner: Object.freeze({
        ...owner,
        residenceCountry: CountryCode.create(owner.residenceCountry.value),
        ...(owner.originCountry === undefined
          ? {}
          : { originCountry: CountryCode.create(owner.originCountry.value) }),
        ...(owner.burialCountry === undefined
          ? {}
          : { burialCountry: CountryCode.create(owner.burialCountry.value) }),
      }),
      revision: Object.freeze({
        ...revision,
        ...(revision.handwrittenSignedDate === undefined
          ? {}
          : { handwrittenSignedDate: LocalDate.create(revision.handwrittenSignedDate.value) }),
      }),
      compartments: Object.freeze({
        public: Object.freeze(
          draft.testament.compartments.public.map((id) => SectionId.create(id.value)),
        ),
        private: Object.freeze(
          draft.testament.compartments.private.map((id) => SectionId.create(id.value)),
        ),
      }),
      nextReviewAt: IsoDateTime.create(draft.testament.nextReviewAt.value),
    }),
    publicAnswers: Object.freeze(draft.publicAnswers.map(revivePublicAnswer)),
    encryptedPrivateVault:
      draft.encryptedPrivateVault === null
        ? null
        : Object.freeze({
            ...draft.encryptedPrivateVault,
            kdfParams: Object.freeze({ ...draft.encryptedPrivateVault.kdfParams }),
          }),
    executor:
      draft.executor === null
        ? null
        : Object.freeze({
            ...draft.executor,
            designatedAt: IsoDateTime.create(draft.executor.designatedAt.value),
            noticeSentAt: reviveInstant(draft.executor.noticeSentAt),
          }),
    emergencyContact: reviveEmergencyContact(draft.emergencyContact),
    updatedAt: IsoDateTime.create(draft.updatedAt.value),
  });
};
